/**
 * Las secciones del panel del club.
 *
 * Van **en la URL** por la misma razón que el club y el alumno abierto
 * (`BFF_BO.md` §5.2): compartir un enlace tiene que llevar al mismo sitio. Sin
 * esto, «mira los pagadores» acaba siendo «entra, dale a pagadores y busca».
 *
 * ## Las secciones de dinero no se enseñan a quien no las puede abrir
 *
 * «Cobros» es `owner` y `admin`; los endpoints de §P.3 y §P.4 responden `403` a un
 * entrenador. Enseñárselas en el menú es exactamente lo que `permisos.ts` dice que
 * no se hace —«un menú lleno de opciones que no funcionan es una aplicación que
 * parece rota»—, así que el menú se filtra por rol.
 *
 * Y `aSeccion` filtra también, porque la sección viaja en la URL: un enlace a
 * `?seccion=cobros` reenviado por WhatsApp al entrenador del club tiene que abrirle
 * el panel, no una pantalla con un `403`.
 *
 * ## Por qué «Cobros» agrupa cinco pantallas y no son cinco secciones
 *
 * Tarifas, descuentos, la remesa, los recibos y los morosos son **un solo trabajo**:
 * lo que hace el administrativo la primera semana de mes. Como cinco entradas del
 * menú superior llenarían la barra de cosas que nadie mira los otros veinte días, y
 * además pasarían de las seis secciones que una cadena de `if` aguanta sin
 * enrutador.
 *
 * Esa es la regla que quedó escrita: **con la sexta sección de primer nivel entra
 * TanStack Router**. No era una excusa para agrupar cosas que no van juntas — si la
 * próxima no encaja en ninguna, toca el enrutador.
 *
 * ## ✅ Pagado · 17-09-2026
 *
 * «Grados» fue la sexta y no encajaba en ninguna, así que tocó el enrutador. Está
 * en `shared/rutas/rutas.tsx`, y este fichero ya **no decide la navegación**: solo
 * dice qué entradas ve cada rol y cómo se llaman.
 *
 * Lo que queda aquí de aquella regla es la lección: una sección de primer nivel más
 * es una decisión, no un elemento de una lista. Y agrupar cosas que no van juntas
 * para no pasar de seis habría sido peor que el problema.
 *
 * ## Por qué «Cumplimiento» es sección propia y no dos pestañas de «El club» · 17-09-2026
 *
 * Aplicando esa misma lección. Las dos parecen «ajustes» desde fuera y no lo son:
 *
 * - «El club» es configurar **lo tuyo** — cómo te llamas, dónde das clase, quién
 *   entra—. Se toca dos veces al año y lo que se cambia es tuyo.
 * - «Cumplimiento» es demostrar **qué haces con los datos de otros**. Se abre cuando
 *   un padre pregunta quién ha visto la ficha de su hija, o cuando hay inspección, o
 *   cuando alguien duda de que nosotros no estemos fisgando. En ese momento hace
 *   falta encontrarlo, no recordar dónde se metió.
 *
 * Y `consents/missing` no es configuración en ningún sentido: es una lista que se
 * persigue igual que los morosos. Enterrarla a tres clics dentro de ajustes es cómo
 * una lista accionable acaba sin usarse nunca.
 *
 * ## ✅ Pagado · 17-09-2026 · la barra cambió de forma
 *
 * Con «Cumplimiento» eran nueve y con §P.9 informes diez, así que tocaba. Y la
 * salida escrita era la que se aplicó: **no se subió el número, cambió la barra.**
 *
 * Cada sección declara su `cadencia`, y eso **es un hecho del producto y no una
 * decisión de maquetación**: a los alumnos y al calendario se entra todos los días;
 * a las sedes del club, dos veces al año. La barra enseña las de diario y el resto
 * va detrás de «Más».
 *
 * Las dos cosas que esto tiene que cumplir para no ser un cajón donde se pierden
 * pantallas:
 *
 * - **La sección en la que estás siempre se ve**, aunque sea de las ocasionales:
 *   `seccionesDeBarra` la sube a la fila visible. Un menú que esconde dónde estás es
 *   peor que un menú apretado.
 * - **«Más» no aparece si no hay nada dentro.** A un entrenador le salen menos
 *   secciones, y un botón que abre una lista vacía es una promesa incumplida.
 *
 * La prueba ya no cuenta secciones —crecerán— sino que la fila visible no pasa de
 * cinco con ningún rol y en ninguna pantalla. Eso es lo que se estropearía.
 */

import {
  puedeCorregirAvisos,
  puedeFirmarElContrato,
  puedeGestionarPersonal,
  puedeVerCuota,
  puedeVerCumplimiento,
  puedeVerElClub,
  puedeVerInformes,
  puedeVerInformesDeGestion,
  puedeVerCompeticion,
  puedeVerLicencias,
  puedeVerMaterial,
  type Rol,
} from '@/club/permisos';

export type Seccion =
  | 'alumnos'
  | 'grupos'
  | 'calendario'
  | 'grados'
  | 'avisos'
  | 'cobros'
  | 'pagadores'
  | 'club'
  | 'cumplimiento'
  | 'informes'
  | 'material'
  | 'competicion';

/**
 * Cada cuánto se entra de verdad en una sección.
 *
 * Es lo que decide qué se ve en la barra, y sale de para qué se usa el panel, no de
 * cuántas entradas caben: la primera semana de mes se vive en «Cobros» y a las sedes
 * se entra cuando el club abre una nueva.
 */
export type Cadencia = 'diaria' | 'ocasional';

export const SECCIONES: readonly {
  id: Seccion;
  etiqueta: string;
  cadencia: Cadencia;
  /** La de dinero. Ver la cabecera. */
  esDeDinero?: true;
  /** Los ajustes del club. El entrenador no entra: ver `permisos.ts`. */
  esDeAjustes?: true;
  /** §P.8. Dueño y administrativo, como los ajustes, pero por otra razón. */
  esDeCumplimiento?: true;
  /** §P.9. **El entrenador sí entra**: la asistencia es suya. Ver `permisos.ts`. */
  esDeInformes?: true;
  /** §P.11. Dueño y administrativo: once de sus doce rutas son de los dos. */
  esDeMaterial?: true;
  /** §P.10. **El entrenador sí entra**: es quien lleva a los niños el sábado. */
  esDeCompeticion?: true;
}[] = [
  { id: 'alumnos', etiqueta: 'Alumnos', cadencia: 'diaria' },
  { id: 'calendario', etiqueta: 'Calendario', cadencia: 'diaria' },
  { id: 'avisos', etiqueta: 'Avisos', cadencia: 'diaria' },
  {
    id: 'cobros',
    etiqueta: 'Cobros',
    cadencia: 'diaria',
    esDeDinero: true,
  },
  // A partir de aquí, lo que no se abre todos los días.
  { id: 'grupos', etiqueta: 'Grupos', cadencia: 'ocasional' },
  { id: 'grados', etiqueta: 'Grados', cadencia: 'ocasional' },
  {
    id: 'informes',
    etiqueta: 'Informes',
    cadencia: 'ocasional',
    esDeInformes: true,
  },
  { id: 'pagadores', etiqueta: 'Pagadores', cadencia: 'ocasional' },
  {
    id: 'club',
    etiqueta: 'El club',
    cadencia: 'ocasional',
    esDeAjustes: true,
  },
  {
    id: 'cumplimiento',
    etiqueta: 'Cumplimiento',
    cadencia: 'ocasional',
    esDeCumplimiento: true,
  },
  /*
   * §P.11 · 19-09-2026. Sección propia, y es una decisión, no un hueco en una lista.
   *
   * El material no encaja en ninguna de las que había: no es un alumno, no es dinero
   * que el club cobra por cuotas, no es un grupo ni un grado, y desde luego no es
   * configuración. Es **un trabajo de septiembre**: publicar lo que vendes, recoger
   * lo que pide cada familia y cursarlo.
   *
   * `ocasional` porque eso es exactamente lo que es —se vive en septiembre y se
   * mira dos veces en marzo—, así que va detrás de «Más» y no ocupa sitio en la
   * barra los otros once meses.
   */
  {
    id: 'material',
    etiqueta: 'Material',
    cadencia: 'ocasional',
    esDeMaterial: true,
  },
  /*
   * §P.10 · 22-09-2026. La otra sección propia de la fase 3, y por la misma razón que
   * «Material»: es un trabajo entero —convocar, pesar, inscribir, apuntar
   * resultados— que no cabe dentro de ninguna de las que había.
   *
   * `ocasional`: se vive la semana antes de cada campeonato y no se abre el resto del
   * mes. Y el club que no compite —el Aikido no compite— simplemente no entra nunca,
   * que es distinto de que le estorbe en la barra.
   */
  {
    id: 'competicion',
    etiqueta: 'Competición',
    cadencia: 'ocasional',
    esDeCompeticion: true,
  },
];

/** Las que este rol puede abrir de verdad. */
export const seccionesPara = (rol: Rol) =>
  SECCIONES.filter(
    (s) =>
      (!s.esDeDinero || puedeVerCuota(rol)) &&
      (!s.esDeAjustes || puedeVerElClub(rol)) &&
      (!s.esDeCumplimiento || puedeVerCumplimiento(rol)) &&
      (!s.esDeInformes || puedeVerInformes(rol)) &&
      (!s.esDeMaterial || puedeVerMaterial(rol)) &&
      (!s.esDeCompeticion || puedeVerCompeticion(rol))
  );

/**
 * Cómo se reparte la barra: lo visible y lo que va detrás de «Más».
 *
 * `camino` es el de la ruta actual, y hace falta: **la sección abierta se sube a la
 * fila visible aunque sea de las ocasionales.** Sin eso, entrar en «Informes» deja
 * la barra sin nada marcado y el usuario sin saber dónde está.
 *
 * `enMas` sale vacío para un rol al que no le queda nada dentro, y el marco no pinta
 * el botón entonces: abrir un menú vacío es peor que no tenerlo.
 */
export function seccionesDeBarra(rol: Rol, camino: string) {
  const suyas = seccionesPara(rol);
  const abierta = suyas.find((s) => camino.startsWith(`/${s.id}`));

  const visibles = suyas.filter(
    (s) => s.cadencia === 'diaria' || s.id === abierta?.id
  );
  const enMas = suyas.filter((s) => !visibles.includes(s));

  return { visibles, enMas };
}

/**
 * Las pantallas de «Cobros», y su orden.
 *
 * **Recibos primero y por defecto**, aunque la remesa sea lo que los crea: se emite
 * una vez al mes y se consultan todos los días. La remesa va después porque es la
 * operación peligrosa, y lo peligroso no se pone donde se cae el dedo.
 */
export type VistaDeCobros =
  | 'recibos'
  | 'morosos'
  | 'cobrado'
  | 'remesa'
  | 'tarifas'
  | 'descuentos'
  | 'devoluciones';

export const VISTAS_DE_COBROS: readonly {
  id: VistaDeCobros;
  etiqueta: string;
}[] = [
  { id: 'recibos', etiqueta: 'Recibos' },
  { id: 'morosos', etiqueta: 'Quién debe' },
  /*
   * «Quién debe» y «lo que ha entrado» son las dos mitades de la misma pregunta y se
   * leen seguidas. Un recibo es lo que el club **pidió**; un cobro, lo que **entró**,
   * y hasta ahora la segunda lista solo se veía abriendo recibo por recibo.
   */
  { id: 'cobrado', etiqueta: 'Lo que ha entrado' },
  { id: 'remesa', etiqueta: 'Remesa del mes' },
  { id: 'tarifas', etiqueta: 'Tarifas' },
  { id: 'descuentos', etiqueta: 'Descuentos' },
  /*
   * La última, y con razón: devolver dinero es raro. La pestaña es el registro para
   * cuadrar con el banco; el botón de devolver está en el cobro de cada recibo,
   * porque una devolución cuelga de un cobro concreto y no de un mes.
   */
  { id: 'devoluciones', etiqueta: 'Devoluciones' },
];

/**
 * Las pantallas de «El club», y su orden.
 *
 * Igual que «Cobros», son un solo trabajo con varias caras: lo que se configura el
 * primer día y se toca dos veces al año. **Los datos primero** porque es lo que se
 * viene a mirar; el personal al final porque es lo único que solo puede el dueño, y
 * una pestaña que a la mitad de la gente le falta no se pone la primera.
 *
 * «La marca» y «Ajustes» van en segundo y tercer lugar (fase 2, 17-09-2026) porque
 * son de las primeras cosas que toca un club nuevo: poner su color es lo que hace que
 * el panel deje de parecer nuestro y empiece a parecer suyo.
 *
 * Seis pestañas es mucho para una barra, y aquí se aguanta por lo que no son: no son
 * seis secciones de primer nivel. Si llegan a ocho, el sitio donde partirlas es
 * «configurar el club» frente a «el personal y sus papeles».
 */
export type VistaDelClub =
  | 'datos'
  | 'marca'
  | 'ajustes'
  | 'sedes'
  | 'disciplinas'
  | 'ficha'
  | 'personal';

export const VISTAS_DEL_CLUB: readonly {
  id: VistaDelClub;
  etiqueta: string;
  /** Solo el dueño. Ver `puedeGestionarPersonal`. */
  soloDueno?: true;
}[] = [
  { id: 'datos', etiqueta: 'Datos' },
  { id: 'marca', etiqueta: 'La marca' },
  { id: 'ajustes', etiqueta: 'Ajustes' },
  { id: 'sedes', etiqueta: 'Sedes' },
  { id: 'disciplinas', etiqueta: 'Disciplinas' },
  /*
   * §P.12 · 19-09-2026. La séptima, y va aquí porque es **configurar lo tuyo**: lo
   * que el club enseña de sí mismo en el buscador, escrito una vez y retocado cuando
   * cambia algo. La otra mitad de §P.12 —las reservas que llegan por esa ficha— no:
   * eso es una bandeja con reloj y vive en «Alumnos».
   *
   * Con siete, la regla escrita arriba sigue en pie: si llegan a ocho, se parte en
   * «configurar el club» y «el personal y sus papeles».
   */
  { id: 'ficha', etiqueta: 'Ficha del buscador' },
  { id: 'personal', etiqueta: 'Personal', soloDueno: true },
];

export const vistasDelClubPara = (rol: Rol) =>
  VISTAS_DEL_CLUB.filter((v) => !v.soloDueno || puedeGestionarPersonal(rol));

/**
 * Las pantallas de «Cumplimiento», y su orden.
 *
 * **Los consentimientos primero**, y no el contrato, aunque el contrato sea lo que
 * se firma el primer día: se firma una vez y no se vuelve a mirar, mientras que «a
 * quién le falta firmar qué» es una lista que se persigue como los morosos. La de
 * entrada es la que se abre cien veces, no la más solemne.
 *
 * El contrato solo lo ve el dueño porque sus dos rutas declaran `own`: no es que sea
 * secreto, es que no hay nada que un administrativo pueda hacer ahí.
 */
export type VistaDeCumplimiento =
  | 'consentimientos'
  | 'solicitudes'
  | 'accesos'
  | 'soporte'
  | 'contrato'
  | 'exportar';

export const VISTAS_DE_CUMPLIMIENTO: readonly {
  id: VistaDeCumplimiento;
  etiqueta: string;
  /** Solo el dueño. Ver `puedeFirmarElContrato`. */
  soloDueno?: true;
}[] = [
  { id: 'consentimientos', etiqueta: 'Consentimientos' },
  /*
   * Las solicitudes en segundo lugar, y no al final: **el plazo es legal**. Una
   * bandeja con un mes de reloj no puede estar detrás de tres pestañas que se abren
   * una vez al año.
   */
  { id: 'solicitudes', etiqueta: 'Solicitudes de datos' },
  { id: 'accesos', etiqueta: 'Registro de accesos' },
  { id: 'soporte', etiqueta: 'Modo soporte' },
  { id: 'contrato', etiqueta: 'Contrato de encargo', soloDueno: true },
  /*
   * La puerta de salida, la última y **visible**: esconderla haría falsa la promesa
   * de que el club se puede ir cuando quiera. `own` a secas, como el contrato.
   */
  { id: 'exportar', etiqueta: 'Llevarte tus datos', soloDueno: true },
];

export const vistasDeCumplimientoPara = (rol: Rol) =>
  VISTAS_DE_CUMPLIMIENTO.filter(
    (v) => !v.soloDueno || puedeFirmarElContrato(rol)
  );

/**
 * Las pestañas de «Informes», y quién las ve.
 *
 * **La asistencia primero**, y no los ingresos: es la que ve todo el mundo —incluido
 * el entrenador, el único informe que le toca— y `/informes` a secas redirige a la
 * primera. Con los ingresos delante, a un entrenador la sección le abriría en una
 * pantalla que no puede ver.
 *
 * Los dos que faltan son fase 3 y 4: el embudo y la comparativa anonimizada. Los
 * cinco de fase 2 —bajas, rentabilidad, ocupación, previsión y horas— entraron el
 * 17-09-2026, y **todos son de gestión**: el entrenador sigue viendo solo la
 * asistencia, que es la única que le toca.
 */
export type VistaDeInformes =
  | 'asistencia'
  | 'ingresos'
  | 'bajas'
  | 'rentabilidad'
  | 'ocupacion'
  | 'prevision'
  | 'horas'
  | 'embudo'
  | 'comparativa';

export const VISTAS_DE_INFORMES: readonly {
  id: VistaDeInformes;
  etiqueta: string;
  /** Los ocho de gestión. Ver `puedeVerInformesDeGestion`. */
  deGestion?: true;
}[] = [
  { id: 'asistencia', etiqueta: 'Asistencia' },
  { id: 'ingresos', etiqueta: 'Ingresos', deGestion: true },
  /*
   * Los cinco de fase 2, en el orden en que un club los usa: primero lo que explica
   * por qué se va la gente, luego lo que dice qué grupo no se sostiene, luego dónde
   * cabe uno nuevo, y al final los dos que se miran una vez al mes.
   */
  { id: 'bajas', etiqueta: 'Bajas', deGestion: true },
  { id: 'rentabilidad', etiqueta: 'Rentabilidad', deGestion: true },
  { id: 'ocupacion', etiqueta: 'Ocupación', deGestion: true },
  { id: 'prevision', etiqueta: 'Previsión de caja', deGestion: true },
  { id: 'horas', etiqueta: 'Horas de entrenador', deGestion: true },
  /*
   * §P.9 fase 3 · 19-09-2026. El último, y no por menos importante: es el informe
   * que solo dice algo cuando el club lleva medio año con la ficha publicada, así
   * que en un club nuevo es el único que abre vacío a propósito. Lo que se mira
   * todos los meses va antes.
   */
  { id: 'embudo', etiqueta: 'Embudo del directorio', deGestion: true },
  /*
   * §P.9 fase 4 · 22-09-2026. El último de los nueve, y el único que puede no existir
   * todavía: hasta que haya cinco clubes del mismo tamaño, no hay media que enseñar
   * sin decir de quién es. Ese estado se explica dentro de la pantalla.
   */
  { id: 'comparativa', etiqueta: 'Comparativa', deGestion: true },
];

export const vistasDeInformesPara = (rol: Rol) =>
  VISTAS_DE_INFORMES.filter(
    (v) => !v.deGestion || puedeVerInformesDeGestion(rol)
  );

/**
 * Las pantallas de «Competición».
 *
 * **Los campeonatos primero**, que es a lo que se entra. Las licencias son el papeleo
 * que hay que tener hecho antes, y se miran una vez al año — salvo el mes en que
 * empiezan a caducar.
 *
 * Y las licencias **no se le enseñan al entrenador**: sus tres rutas son `own` y
 * `adm`. Es la única excepción de esta sección, donde por lo demás el entrenador entra
 * en todo.
 */
export type VistaDeCompeticion = 'campeonatos' | 'licencias';

export const VISTAS_DE_COMPETICION: readonly {
  id: VistaDeCompeticion;
  etiqueta: string;
  /** Solo dueño y administrativo. Ver `puedeVerLicencias`. */
  sinEntrenador?: true;
}[] = [
  { id: 'campeonatos', etiqueta: 'Campeonatos' },
  { id: 'licencias', etiqueta: 'Licencias', sinEntrenador: true },
];

export const vistasDeCompeticionPara = (rol: Rol) =>
  VISTAS_DE_COMPETICION.filter(
    (v) => !v.sinEntrenador || puedeVerLicencias(rol)
  );

/**
 * Las pantallas de «Material», y su orden.
 *
 * **El catálogo primero**, porque sin artículos publicados no hay nada que pedir: es
 * lo que un club tiene que hacer antes de nada. Los pedidos después —es lo que se
 * abre en septiembre— y las solicitudes en tercer lugar, que es donde se trabaja
 * durante esas dos semanas.
 *
 * La segunda mano al final y por lo que es: un tablón que se modera cuando alguien
 * avisa, no un sitio donde se entra a hacer algo. Es fase 4 y compite con la venta de
 * material del club, así que cada club lo apaga desde sus ajustes.
 */
export type VistaDeMaterial =
  'catalogo' | 'pedidos' | 'solicitudes' | 'segunda-mano';

export const VISTAS_DE_MATERIAL: readonly {
  id: VistaDeMaterial;
  etiqueta: string;
}[] = [
  { id: 'catalogo', etiqueta: 'Catálogo' },
  { id: 'pedidos', etiqueta: 'Pedidos agrupados' },
  { id: 'solicitudes', etiqueta: 'Solicitudes' },
  { id: 'segunda-mano', etiqueta: 'Segunda mano' },
];

/**
 * Las pantallas de «Alumnos».
 *
 * **La lista es la de entrada y se queda en `/alumnos` a secas**, sin sufijo: es la
 * dirección que ya está en enlaces compartidos y a la que vuelve media aplicación al
 * cerrar una ficha. Por eso su `id` es la cadena vacía, que es feo de leer y más
 * barato que romper una URL.
 *
 * «Clases de prueba» es §P.12 y está aquí porque **una reserva es un alumno que
 * todavía no existe**: lo que la cierra es darlo de alta. El razonamiento largo está
 * en `MarcoDeAlumnos`.
 */
export type VistaDeAlumnos = '' | 'pruebas';

export const VISTAS_DE_ALUMNOS: readonly {
  id: VistaDeAlumnos;
  etiqueta: string;
}[] = [
  { id: '', etiqueta: 'Alumnos' },
  { id: 'pruebas', etiqueta: 'Clases de prueba' },
];

/**
 * Las pantallas de «Calendario».
 *
 * **Las clases primero**, que es a lo que se entra. «Quién ha avisado» es la mitad
 * que faltaba: las justificaciones de las familias solo se veían abriendo clase por
 * clase, y el administrativo que quiere cuadrar la semana no tenía dónde mirar.
 *
 * No llevan filtro de rol: las dos rutas son `own`, `adm` y `coa`.
 */
export type VistaDelCalendario = 'clases' | 'asistencia' | 'ausencias';

export const VISTAS_DEL_CALENDARIO: readonly {
  id: VistaDelCalendario;
  etiqueta: string;
}[] = [
  { id: 'clases', etiqueta: 'Las clases' },
  /*
   * «Quién vino» va en medio, entre las clases y los avisos, porque es lo que se
   * mira **después** de que la clase pase y **antes** de llamar a nadie. Es la
   * consulta transversal de §P.2: hasta el 18-09-2026 la asistencia solo se podía
   * mirar clase por clase, y quien lleva la secretaría pregunta por una semana.
   */
  { id: 'asistencia', etiqueta: 'Quién vino' },
  { id: 'ausencias', etiqueta: 'Quién ha avisado' },
];

/**
 * Las pantallas de «Grados».
 *
 * **Los sistemas primero**: sin escalera no hay a qué examinarse, y es lo que un club
 * nuevo tiene que hacer antes de nada. Los exámenes se convocan dos o tres veces al
 * curso; la escalera se define una vez y se toca cada dos años.
 *
 * **El temario al final**, y no por menos importante: es lo que se escribe una vez y
 * se consulta mucho, mientras que las otras dos son trabajo que se hace. Va en
 * «Grados» y no en una sección propia porque un temario está organizado por
 * cinturones: fuera de la escalera no significa nada.
 *
 * Sin filtro de rol: las rutas de las tres son `own`, `adm` y `coa`. Lo que el
 * entrenador no puede es **quitar** una técnica, y eso se resuelve dentro de la
 * pantalla —ver `puedeQuitarDelTemario`— y no escondiéndole la pestaña.
 */
export type VistaDeGrados = 'sistemas' | 'examenes' | 'temario';

export const VISTAS_DE_GRADOS: readonly {
  id: VistaDeGrados;
  etiqueta: string;
}[] = [
  { id: 'sistemas', etiqueta: 'Sistemas de grados' },
  { id: 'examenes', etiqueta: 'Exámenes' },
  { id: 'temario', etiqueta: 'Temario' },
];

/**
 * Las pantallas de «Avisos».
 *
 * **Los avisos primero**: es lo que se viene a hacer. El chat después, el muro —que
 * se revisa, no se escribe— y el horario de silencio al final, que se toca una vez.
 *
 * El horario es `own` y `adm`: es una decisión del club sobre cuándo interrumpe a sus
 * familias, no de quien da clase.
 */
export type VistaDeComunicacion = 'publicados' | 'chat' | 'muro' | 'silencio';

export const VISTAS_DE_COMUNICACION: readonly {
  id: VistaDeComunicacion;
  etiqueta: string;
  /** Dueño y administrativo. Ver `puedeCorregirAvisos`, que es la misma pareja. */
  sinEntrenador?: true;
}[] = [
  { id: 'publicados', etiqueta: 'Avisos' },
  { id: 'chat', etiqueta: 'Chat' },
  { id: 'muro', etiqueta: 'El muro' },
  { id: 'silencio', etiqueta: 'Horario de silencio', sinEntrenador: true },
];

export const vistasDeComunicacionPara = (rol: Rol) =>
  VISTAS_DE_COMUNICACION.filter(
    (v) => !v.sinEntrenador || puedeCorregirAvisos(rol)
  );
